import { useCallback } from 'react';
import {
  clearEstimateReport,
  EstimateReportData,
  setEstimateReport,
  setEstimateReportError,
  setEstimateReportLoading,
} from '../redux/estimateReportSlice';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { IncorrectTimeNote, Issue, RecordFunc, ReportParams } from '../utils/model';
import { whiteListUsers } from '../utils/whiteListUsers';
import { getProjectIssuesGql } from '../gitlabApi/gqlApi/getProjectIssues';
import { getIssueById } from '../gitlabApi/gqlApi/getIssue';

export const useEstimateReportGenerate = (): RecordFunc => {
  const username = useAppSelector((s) => s.user.user?.username?.toLowerCase());
  const wl = whiteListUsers.indexOf(username ?? '') >= 0;
  const dispatch = useAppDispatch();
  const action = useCallback(
    async ({ from, to, product, projects, validateNotes }: ReportParams) => {
      const addIncorrectTimeNote = (incorrectTimeNote: IncorrectTimeNote) => {
        alert(
          `Possible incorrect time note: ${incorrectTimeNote.noteId} for issue ${incorrectTimeNote.shortIssue.title}. Check console`,
        );
        console.error(incorrectTimeNote);
      };

      dispatch(clearEstimateReport());
      if (!wl) {
        dispatch(setEstimateReportError('No access. Ask Philipp'));
        return;
      }
      if (!product.pmProjectPath) {
        dispatch(setEstimateReportError("This project doesn't have pm project"));
        return;
      }

      try {
        dispatch(setEstimateReportLoading('Loading...'));

        let allPmIssues: Issue[] = [];
        let allDevIssues: Issue[] = [];
        const pmIssuesMap: Record<string, { pm: Issue; dev: Issue[] }> = {};

        const EstimateReportData: EstimateReportData = {
          linkedMap: {},
          devIssues: [],
        };
        for (const project of product.projects) {
          dispatch(setEstimateReportLoading(project.name));
          const setProgress = (progress: string) =>
            dispatch(setEstimateReportLoading(`${project.name}: ${progress}`));
          const issues = await getProjectIssuesGql({
            project,
            from,
            to,
            addIncorrectTimeNote,
            setProgress,
            projects,
            validateNotes,
          });
          if (product.pmProjectPath === project.fullPath) {
            allPmIssues.push(...issues);
          } else {
            allDevIssues.push(...issues);
          }
        }
        dispatch(setEstimateReportLoading('Processing issues'));

        for (const issue of allPmIssues) {
          pmIssuesMap[issue.id] = { pm: issue, dev: [] };
        }

        for (const issue of allDevIssues) {
          // remove spies
          if (issue.project.fullPath === product.pmProjectPath) {
            alert(
              `PM issue found in dev issues: ${issue.title}, ${issue.id}, send screenshot to Philipp`,
            );
            continue;
          }

          const pmDevIssue = issue.links?.find(
            (l) => l.project.fullPath === product.pmProjectPath,
          );
          const pmIssueId = pmDevIssue?.id;
          if (pmIssueId) {
            if (!pmIssuesMap[pmIssueId]) {
              console.error(
                `PM issue not found: ${issue.title}, ${issue.id}. Loading it`,
              );
              dispatch(
                setEstimateReportLoading(`Loading unlinked pm issue: ${pmDevIssue.id}`),
              );
              const pmIssue = await getIssueById({
                id: pmIssueId,
                addIncorrectTimeNote,
                validateNotes,
                projects,
                from,
                to,
              });
              if (!pmIssue) {
                alert(`PM issue not found: ${issue.title}, ${issue.id}. Skipping`);
                continue;
              }
              allPmIssues.push(pmIssue);
              pmIssuesMap[pmIssue.id] = { pm: pmIssue, dev: [] };
            }
            pmIssuesMap[pmIssueId].dev.push(issue);
          } else {
            EstimateReportData.devIssues.push(issue);
          }
        }

        for (const key in pmIssuesMap) {
          if (Object.prototype.hasOwnProperty.call(pmIssuesMap, key)) {
            const pmIssue = pmIssuesMap[key];
            for (const devIssueLinkedToPm of pmIssue.pm.links ?? []) {
              let devTargetIssue = pmIssue.dev.find(
                (devIssue) => devIssue.id === devIssueLinkedToPm.id,
              );
              if (!devTargetIssue) {
                devTargetIssue = allDevIssues.find(
                  (devIssue) => devIssue.id === devIssueLinkedToPm.id,
                );
                if (devTargetIssue) {
                  pmIssue.dev.push(devTargetIssue);
                }
              }
              if (!devTargetIssue) {
                dispatch(
                  setEstimateReportLoading(
                    `Loading unloaded dev issue: ${devIssueLinkedToPm.id} for pm issue: ${pmIssue.pm.title}`,
                  ),
                );
                const issue = await getIssueById({
                  id: devIssueLinkedToPm.id,
                  addIncorrectTimeNote,
                  validateNotes,
                  projects,
                  from,
                  to,
                });
                if (!issue) {
                  alert(`dev issue not found: ${devIssueLinkedToPm.id}. Skipping`);
                  continue;
                }
                pmIssue.dev.push(issue);
              }
            }

            let milestone = pmIssue.pm.milestone; // Empty string can be Record key
            if (!milestone) {
              milestone = {
                title: '',
                createdAt: new Date(0),
              };
            }
            if (!EstimateReportData.linkedMap[milestone.title]) {
              EstimateReportData.linkedMap[milestone.title] = { data: [], milestone };
            }
            const total = pmIssue.dev.reduce(
              (acc, devIssue) => acc + (devIssue.time_stats?.total_time_spent ?? 0),
              0,
            );
            EstimateReportData.linkedMap[milestone.title].data.push({
              pmIssue: pmIssue.pm,
              devIssues: pmIssue.dev,
              estimate: pmIssue.pm.time_stats?.time_estimate ?? 0,
              total,
            });
          }
        }
        dispatch(setEstimateReport(EstimateReportData));
      } catch (err: any) {
        dispatch(setEstimateReportError(err.message));
      } finally {
        dispatch(setEstimateReportLoading(undefined));
      }
    },
    [dispatch, wl],
  );
  return action;
};
